import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Users, Briefcase } from 'lucide-react';
import { buildBootcampUrl } from '../utils/leadCapture';

export default function BootcampCTA({ variant = 'default', context = 'general' }) {
  const bootcampUrl = buildBootcampUrl({
    utmMedium: 'tekyu-app',
    utmCampaign: context 
  });

  const variants = {
    default: {
      container: 'rounded-2xl border-2 border-indigo-500/30 bg-gradient-to-br from-indigo-950/40 to-slate-900/60 p-6',
      title: 'text-2xl font-extrabold text-white mb-3',
      description: 'text-slate-300 mb-4',
      button: 'inline-flex items-center justify-center rounded-xl px-6 py-3 font-bold transition-all bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90 shadow-lg shadow-indigo-500/25',
    },
    inline: {
      container: 'rounded-xl border border-indigo-500/20 bg-indigo-950/20 p-4',
      title: 'text-lg font-bold text-white mb-2',
      description: 'text-sm text-slate-300 mb-3',
      button: 'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/30 transition-colors',
    },
    banner: {
      container: 'rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-950/30 to-purple-950/30 p-4',
      title: 'text-base font-bold text-white mb-1',
      description: 'text-xs text-slate-300 mb-2',
      button: 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500 text-white hover:opacity-90',
    },
  };

  const styles = variants[variant] || variants.default;

  const messages = {
    roadmap: {title:'Want a structured route into DevSecOps and AI security?',description:'Explore the Cyber Agoge bootcamp for live teaching, guided practicals and project support.',highlight:'Two starting points: new to tech or already experienced'},
    project: {title:'Build with guided support',description:'Explore Cyber Agoge for live teaching and supported practical work. Check the programme to see which projects are included.',highlight:'Learning and portfolio development'},
    portal: {title:'Ready for structured learning?',description:'Cyber Agoge combines live teaching with guided portal exercises and project support.',highlight:'Explore the current programme'},
    pricing: {title:'Looking for live teaching?',description:'Tekyu offers self-paced roadmaps and project guides. Cyber Agoge is a separate bootcamp with live sessions and supported practicals.',highlight:'Separate programme and enrolment'},
    general: {title:'Join Cyber Agoge Bootcamp',description:'Explore live DevSecOps and AI security teaching, guided exercises and project support.',highlight:'Build skills you can demonstrate'},
  };

  const message = messages[context] || messages.general;

  return (
    <div className={styles.container}>
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-indigo-500/20 p-2 border border-indigo-500/30">
          <Sparkles className="h-5 w-5 text-indigo-300" />
        </div>
        <div className="flex-1">
          <h3 className={styles.title}>{message.title}</h3>
          <p className={styles.description}>{message.description}</p>
          {message.highlight && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-green-300 text-sm font-semibold mb-4">
              {message.highlight}
            </div>
          )}
          {variant === 'default' && (
            <div className="flex items-center gap-4 text-sm text-indigo-300 mb-4">
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>Live mentorship</span>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="h-4 w-4" />
                <span>Practical project work</span>
              </div>
            </div>
          )}
          <a
            href={bootcampUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.button}
          >
            {variant === 'banner' ? (
              <>
                Learn More
                <ArrowRight className="h-3 w-3" />
              </>
            ) : (
              <>
                Learn More About Cyber Agoge Bootcamp
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </a>
        </div>
      </div>
    </div>
  );
}
